import { Stack, Text, textVariants } from '@etg/wings';
import { css } from '@eti/styles';
import { date as dateUtil } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { isEqual } from 'date-fns';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';

const relativeStyles = css`
  position: relative;
`;

interface RouteInfoProps {
  dataTestId: string;
  route: {
    destination: { cityName: string; cityCode: string };
    origin: { cityName: string; cityCode: string };
    arrivedAt?: string;
    departuredAt?: string;
  };
}

const RouteInfo = ({ dataTestId, route }: RouteInfoProps) => {
  const { t } = useTranslation();
  const { origin, destination } = route;
  const departureDate = route.departuredAt
    ? dateUtil.format(route.departuredAt, 'E dd MMM yyy')
    : null;
  const departureTime = route.departuredAt ? dateUtil.format(route.departuredAt, 'HH:mm') : null;
  const arrivalDate = route.arrivedAt ? dateUtil.format(route.arrivedAt, 'E dd MMM yyy') : null;
  const arrivalTime = route.arrivedAt ? dateUtil.format(route.arrivedAt, 'HH:mm') : null;
  return (
    <Stack className={relativeStyles} spacing={4}>
      <div className={screenReadersOnlyStyles}>
        {t(
          'ScreenReader.From.To.Text',
          `${origin.cityName} ${origin.cityCode}`,
          `${destination.cityName} ${destination.cityCode}`,
        )}
      </div>
      <Text
        aria-hidden="true"
        data-testid={`${dataTestId}-origin-destination`}
        variant={textVariants._16_BOLD}
      >
        <bdi>
          {origin.cityName} {origin.cityCode} - {destination.cityName} {destination.cityCode}
        </bdi>
      </Text>
      {route.departuredAt && route.arrivedAt && (
        <>
          <div className={screenReadersOnlyStyles}>
            {isEqual(departureDate, arrivalDate)
              ? `${departureDate} ${t('ScreenReader.Departure.Text')} ${departureTime} ${t(
                  'ScreenReader.Arrival.Text',
                )} ${arrivalTime}`
              : `${t('ScreenReader.Departure.Text')} ${departureDate} ${departureTime} ${t(
                  'ScreenReader.Arrival.Text',
                )} ${arrivalDate} ${arrivalTime}`}
          </div>
          <Text aria-hidden="true" data-testid={`${dataTestId}-departure-arrival`}>
            {isEqual(departureDate, arrivalDate) ? (
              <bdi>
                {departureDate}, {departureTime} - {arrivalTime}
              </bdi>
            ) : (
              <bdi>
                {departureDate}, {departureTime} - {arrivalDate}, {arrivalTime}
              </bdi>
            )}
          </Text>
        </>
      )}
    </Stack>
  );
};

export default RouteInfo;
